import { pipe, isEmpty, trim, toString, find } from 'lodash/fp';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import objectSupport from 'dayjs/plugin/objectSupport';
import timezone from 'dayjs/plugin/timezone';
import i18n from 'i18n';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { fetchAttachment } from 'services/searchEmail.service';

import {
  VIEW_DASHBOARD_LEFT_NAV,
  LANGUAGE,
  VIEW_MEMBERS_LEFT_NAV,
  VIEW_REF_CHANNEL_LEFT_NAV,
  USERSROLE,
  VIEW_REPORT_LEFT_NAV,
  VIEW_HOME_LEFT_NAV,
  VIEW_SUPPORT_TOOLS_LEFT_NAV,
  AddressType,
  VIEW_MANAGE_USERS_LEFT_NAV,
  VIEW_CONFIGURATION_LEFT_NAV,
  VIEW_MANAGE_ACCESS_LEFT_NAV
} from 'types';
import { isNull, isUndefined, isObject } from 'lodash';
dayjs.extend(utc);
dayjs.extend(objectSupport);
dayjs.extend(minMax);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const now = dayjs().utc().local();

export const isEmptyStr = pipe(toString, trim, isEmpty);

export const toLanguageType = (language: string = 'en'): LANGUAGE => {
  switch (language) {
    case 'cn':
      return LANGUAGE.CN;
    default:
      return LANGUAGE.EN;
  }
};

export const isInValidEmail = (email: string) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return !re.test(email);
};

export const isNumberOnly = (value: string) => {
  const re = /^\d*\.?\d*$/;
  return re.test(value);
};

export const isObjectEmpty = (obj: any) =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

export const isObjectPropertiesEmpty = (obj: Object | any) => {
  const res = Object.values(obj).indexOf('') >= 0;
  return res;
};

export const isNumeric = (n: any) => !isNaN(parseFloat(n)) && isFinite(n);

export const isValidUserRole = (
  userRoleGroup:
    | typeof USERSROLE
    | typeof VIEW_MEMBERS_LEFT_NAV
    | typeof VIEW_REF_CHANNEL_LEFT_NAV
    | typeof VIEW_DASHBOARD_LEFT_NAV
    | typeof VIEW_REPORT_LEFT_NAV
    | typeof VIEW_HOME_LEFT_NAV
    | typeof VIEW_SUPPORT_TOOLS_LEFT_NAV
    | typeof VIEW_MANAGE_USERS_LEFT_NAV
    | typeof VIEW_CONFIGURATION_LEFT_NAV
    | typeof VIEW_MANAGE_ACCESS_LEFT_NAV
    | string[],
  currentUserRole: any
): boolean => Object.values(userRoleGroup).includes(currentUserRole) || false;

export const toLocalDateTimeFormat = (val: any) => {
  const date = !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().local().format('DD/MM/YYYY h:mm a').toString()
    : '';
  return date;
};

export const toLocalDateTimeFormatWithSeconds = (val: any) => {
  const date = !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().local().format('DD/MM/YYYY hh:mm:ss a').toString()
    : '';
  return date;
};

export const toLocalDateFormat = (val: any) =>
  !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().local().format('DD/MM/YYYY').toString()
    : '';

export const toLocalTimeFormat = (val: any) =>
  !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().local().format('h:mm a').toString()
    : '';

export const toDateFormatWithTimezone = (
  val: any,
  timezone: string = 'Australia/Sydney'
) =>
  !(isNull(val) || isUndefined(val))
    ? dayjs(val).tz(timezone).local().format('DD/MM/YYYY').toString()
    : '';

export const toTimeFormatWithTimezone = (
  val: any,
  timezone: string = 'Australia/Sydney'
) =>
  !(isNull(val) || isUndefined(val))
    ? dayjs(val).tz(timezone).format('h:mm a').toString()
    : '';

export const toDateAPIFormat = (val: any) => dayjs(val).format('YYYY-MM-DD');

export const toDateFormat = (val: any) =>
  !(isNull(val) || isUndefined(val)) && dayjs(val).utc().format('DD/MM/YYYY');

export const toTitleCase = (value: string) =>
  value &&
  value.replace(/\w\S*/g, (txt) => {
    if (txt.indexOf("'") === 1) {
      return `${txt.charAt(0).toUpperCase()}'${txt
        .charAt(2)
        .toUpperCase()}${txt.substr(3).toLowerCase()}`;
    } else if (txt.toLowerCase().indexOf('mc') === 0) {
      return `Mc${txt.charAt(2).toUpperCase()}${txt.substr(3).toLowerCase()}`;
    }
    return `${txt.charAt(0).toUpperCase()}${txt.substr(1).toLowerCase()}`;
  });

export const convertStringToDateFormat = (
  date: string | Date,
  format: string
) => (isObject(date) ? dayjs(date).format(format) : date);

export const debounce = (func: Function, wait = 50) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return function (...args: Parameters<any>) {
    const doLater = function () {
      timeoutId = undefined;
      func(args);
    };

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(doLater, wait);
  };
};

export const getFullAddress = (
  address: AddressType,
  partial: boolean = false
) => {
  if (!address) {
    return '';
  }
  const { line1, line2, locality, postcode, state } = address;
  if (partial) {
    return `
          ${locality ? toTitleCase(locality) : ''} ${state ? state : ''}
          `;
  }
  return `
          ${line1 ? toTitleCase(line1) : ''}${
    line2 ? `, ${toTitleCase(line2)}` : ''
  }${line1 || line2 ? ',' : ''}
          ${locality ? toTitleCase(locality) : ''} ${state ? state : ''}
          ${postcode ? postcode : ''}
          `;
};

/*
 * Params
 * extDays = no of days to be extended. For ex: 28 days
 * 17/12/2020[ExtendedDate] and 16/12/2020[selectedDate] = true
 */
export const isDateAfterExtDate = (selectedDate: any, extDays: number) => {
  const extendedDate = dayjs().local().add(extDays, 'day');
  return !extendedDate.isAfter(selectedDate, 'date');
};
/*
 * Params
 * extDays = no of days to be extended. For ex: # Months
 * Month (January as 1, December as 12)
 */
export const isDateAfterExtMonth = (selectedDate: any, extMonths: number) => {
  const extendedDate = now.add(extMonths, 'month').subtract(1, 'day');
  return !extendedDate.isAfter(selectedDate, 'date');
};
export const isSelectedTodayDate = (date: any) => {
  return now.isSame(date, 'date');
};

export const isAfterTodayDate = (date: any) => {
  const endOfYesterday = now.subtract(1, 'day').endOf('day');
  return endOfYesterday.isBefore(date);
};

export const getDateAfterMonths = (extMonths: number) => {
  return now.add(extMonths, 'month').subtract(1, 'day').utc().local();
};

export const getDateAfterDays = (days: number) => {
  return now.add(days, 'day');
};

export const getDateBeforeDays = (days: number) => {
  return now.subtract(days, 'day');
};

export const getNewDate = (date: any = undefined) => {
  return dayjs(date);
};

export const getTodayDate = () => Utils.toLocalDateFormat(now);

export const getTimeZone = () => dayjs.tz.guess();

export const getDaysFromNow = (val: any) => {
  return now.diff(val, 'day');
};

export const getDiffDates = (date1: any, date2: any) => {
  return dayjs(date1).diff(dayjs(date2));
};

export const findOjectKeyVal = (obj: Object, findObj: any) =>
  find(obj, findObj);

export const roleMapper = (role: string) => {
  switch (role.toUpperCase()) {
    case 'DBYDADMIN':
      return 'BYDA Administrator';
    case 'ENQUIRER':
      return 'Enquirer';
    case 'SYSADMIN':
      return 'Support';
    case 'AOVIEWER':
      return 'Asset owner (Viewer)';
    case 'AOADMIN':
      return 'Asset owner (Admin)';
    default:
      return null;
  }
};

export const UNKNOWN_ERROR = 'Unknown error';
const optimizeErrMsg = (errorList: any, path: string, error: any): string => {
  if (!error.message) {
    console.error(error);
    return 'Unknown error';
  }
  const msg = error.message;
  let translatedMessage = '';
  Object.keys(errorList).forEach((error) => {
    if (msg.includes(error)) {
      translatedMessage = msg.replace(error, i18n.t(`${path}.${error}`));
    }
  });
  // if no error can be mapped, better to show unknown error then system error
  return translatedMessage || 'Unknown error';
};

export const validURL = (str: string) => {
  var pattern = new RegExp(
    '^(https://www.|https://)?[a-z0-9]+([-.]{1}[a-z0-9]+)*.[a-z]{2,5}(:[0-9]{1,5})?(/.*)?$'
  ); // fragment locator
  return !!pattern.test(str);
};

export const getEmailAttachment = async ({
  url,
  attachmentName,
  errorCallBack
}: {
  url: string;
  attachmentName: string;
  errorCallBack?: Function;
}) => {
  try {
    const response = await fetchAttachment({ url });
    const blob = await response.blob();
    const localUrl = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = localUrl;
    link.setAttribute('download', `${attachmentName}`);
    link.click();
    link?.parentNode?.removeChild(link);
  } catch (err) {
    typeof errorCallBack === 'function' && errorCallBack(err);
  }
};

export const notificationTypeDescription = (notificationType: string) => {
  switch (notificationType?.toUpperCase()) {
    case 'ALL_RECEIVED':
      return 'Notify me when all responses are received';
    case 'NOTIFY_RESPONSE_RECEIVED':
      return 'Notify me on every response received';
    case 'FOWWARD_ALL_RESPONSE':
      return 'Forward each response to me as it is received';
    default:
      return 'Forward each response to me as it is received';
  }
};

export const isValidRegex = (str: string) => {
  try {
    new RegExp(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const checkFileExtensionRegex = (
  filename: string,
  extension: string
) => {
  const regex = new RegExp('\\.' + extension + '$', 'i');
  return regex.test(filename);
};

const Utils = {
  debounce,
  isEmptyStr,
  toLanguageType,
  isInValidEmail,
  isNumberOnly,
  isObjectEmpty,
  isObjectPropertiesEmpty,
  isNumeric,
  isValidUserRole,
  toLocalDateTimeFormat,
  toLocalDateFormat,
  toLocalTimeFormat,
  toTimeFormatWithTimezone,
  toDateFormatWithTimezone,
  toTitleCase,
  convertStringToDateFormat,
  getFullAddress,
  isDateAfterExtDate,
  isSelectedTodayDate,
  isAfterTodayDate,
  getDateAfterMonths,
  getTodayDate,
  isDateAfterExtMonth,
  getDateAfterDays,
  getDateBeforeDays,
  getNewDate,
  getTimeZone,
  getDaysFromNow,
  findOjectKeyVal,
  roleMapper,
  getDiffDates,
  optimizeErrMsg,
  UNKNOWN_ERROR,
  toDateFormat,
  toDateAPIFormat,
  getEmailAttachment,
  validURL,
  checkFileExtensionRegex
};

export default Utils;
